import React, { useState, useEffect, useRef } from 'react';
import { useCms } from '@/context/CmsContext';
import { useCmsList } from '@/hooks/useCmsList';
import { bulkImportListItems } from '@/services/cms';
import styles from './index.module.css';

interface Winner {
    id?: number;
    winner_id?: number;
    promotion: string;
    first_name: string;
    last_name: string;
    city: string;
    state: string;
    prize: string;
}

interface GiveawayGroup {
    [key: string]: Winner[];
}

export default function AccountWinners() {
    const { isAdmin, isEditMode } = useCms();
    const { items: cmsWinners, loading: cmsLoading, add, remove, refetch } = useCmsList<Winner>('previous-winners');
    const [legacyWinners, setLegacyWinners] = useState<Winner[]>([]);
    const [legacyLoading, setLegacyLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [csvStatus, setCsvStatus] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Fetch from legacy API as fallback
    useEffect(() => {
        fetchLegacyWinners();
    }, []);

    const fetchLegacyWinners = async () => {
        const API_URL = 'https://fueling-discovery-api.burcs.workers.dev/api/previous-winners';
        const LIMIT = 50;
        let page = 0;
        let all: Winner[] = [];

        try {
            while (true) {
                const response = await fetch(`${API_URL}?page=${page}&limit=${LIMIT}`);
                if (!response.ok) break;
                const data = await response.json();
                const items = data?.response?.items || [];
                if (!items.length) break;
                all = all.concat(items);
                if (items.length < LIMIT) break;
                page += 1;
            }
            setLegacyWinners(all);
        } catch (error) {
            console.error('Error fetching winners:', error);
        } finally {
            setLegacyLoading(false);
        }
    };

    const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setCsvStatus('Parsing...');
        const text = await file.text();
        const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);

        // Detect header row
        const firstLine = lines[0].toLowerCase();
        const hasHeader = firstLine.includes('promotion') || firstLine.includes('first_name') || firstLine.includes('first name');
        const dataLines = hasHeader ? lines.slice(1) : lines;

        const items: Array<Record<string, any>> = [];
        for (const line of dataLines) {
            // Handle quoted CSV fields
            const fields = line.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g)?.map(f => f.replace(/^"|"$/g, '').trim()) || [];
            if (fields.length < 4) continue;

            // Support: promotion, first_name, last_name, city, state, prize
            items.push({
                promotion: fields[0] || '',
                first_name: fields[1] || '',
                last_name: fields[2] || '',
                city: fields[3] || '',
                state: fields[4] || '',
                prize: fields[5] || '',
            });
        }

        if (items.length === 0) {
            setCsvStatus('No valid rows found. Expected: promotion, first_name, last_name, city, state, prize');
            return;
        }

        setCsvStatus(`Importing ${items.length} winners...`);
        try {
            const count = await bulkImportListItems('previous-winners', items);
            setCsvStatus(`Imported ${count} winners!`);
            refetch();
            setTimeout(() => setCsvStatus(''), 3000);
        } catch (err) {
            setCsvStatus('Import failed. Check console.');
            console.error(err);
        }

        // Reset file input
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Use CMS winners if available, otherwise legacy
    const winners = cmsWinners.length > 0 ? cmsWinners : legacyWinners;
    const loading = cmsWinners.length > 0 ? cmsLoading : legacyLoading;

    const giveaways = winners.reduce<GiveawayGroup>((acc, item) => {
        acc[item.promotion] = acc[item.promotion] || [];
        acc[item.promotion].push(item);
        return acc;
    }, {});

    return (
        <div className={styles.container}>
            <div className={styles.title}>Previous Winners</div>
            <div className={styles.subtitle}>Below are the past Kwik Fill Giveaway Winners.</div>

            {isAdmin && isEditMode && (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                            onClick={() => setShowAddForm(!showAddForm)}
                            style={{
                                background: '#3b82f6', color: 'white', border: 'none',
                                padding: '8px 16px', borderRadius: 8, fontSize: 13,
                                fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
                            }}
                        >
                            + Add Winner
                        </button>

                        <label style={{
                            background: '#8b5cf6', color: 'white', border: 'none',
                            padding: '8px 16px', borderRadius: 8, fontSize: 13,
                            fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
                            display: 'inline-block',
                        }}>
                            Upload CSV
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv"
                                onChange={handleCsvUpload}
                                style={{ display: 'none' }}
                            />
                        </label>

                        {csvStatus && (
                            <span style={{
                                fontSize: 13, fontFamily: 'system-ui',
                                color: csvStatus.includes('Imported') ? '#22c55e' : csvStatus.includes('failed') ? '#ef4444' : '#6b7280',
                            }}>
                                {csvStatus}
                            </span>
                        )}
                    </div>

                    <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af', fontFamily: 'system-ui' }}>
                        CSV format: promotion, first_name, last_name, city, state, prize (header row optional)
                    </div>

                    {showAddForm && (
                        <AddWinnerForm
                            onSubmit={async (data) => {
                                await add(data);
                                setShowAddForm(false);
                            }}
                            onCancel={() => setShowAddForm(false)}
                        />
                    )}
                </div>
            )}

            {loading && <div>Loading...</div>}

            {Object.entries(giveaways).map(([giveawayName, groupWinners], index) => (
                <AccordionItem
                    key={index}
                    title={giveawayName}
                    winners={groupWinners}
                    canEdit={isAdmin && isEditMode}
                    onRemove={remove}
                />
            ))}
        </div>
    );
}

interface AccordionItemProps {
    title: string;
    winners: Winner[];
    canEdit: boolean;
    onRemove: (id: number) => Promise<void>;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, winners, canEdit, onRemove }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className={styles.section_container}>
            <div className={styles.row}>
                <div className={styles.giveaway_name}>{title}</div>
                <div className={styles.toggle_visibility} onClick={() => setIsOpen(!isOpen)}>
                    {isOpen ? 'Hide' : 'View'} Winners
                </div>
            </div>

            {isOpen && (
                <div className={styles.details}>
                    {winners.map((winner, index) => (
                        <div key={index} className={styles.details_list} style={{ position: 'relative' }}>
                            <div className={styles.name}>
                                {winner.first_name} {winner.last_name} ({winner.city}, {winner.state})
                            </div>
                            <div> - {winner.prize}</div>
                            {canEdit && winner.id && (
                                <button
                                    onClick={() => onRemove(winner.id!)}
                                    style={{
                                        position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
                                        background: '#ef4444', color: 'white', border: 'none',
                                        width: 20, height: 20, borderRadius: '50%', fontSize: 12,
                                        cursor: 'pointer', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    title="Remove winner"
                                >
                                    &times;
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

interface AddWinnerFormProps {
    onSubmit: (data: Omit<Winner, 'id'>) => Promise<void>;
    onCancel: () => void;
}

const AddWinnerForm: React.FC<AddWinnerFormProps> = ({ onSubmit, onCancel }) => {
    const [form, setForm] = useState({
        promotion: '', first_name: '', last_name: '',
        city: '', state: '', prize: '',
    });

    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0',
        borderRadius: 8, fontSize: 14, fontFamily: 'system-ui', boxSizing: 'border-box',
    };
    const labelStyle: React.CSSProperties = {
        display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 4,
        fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em',
    };

    return (
        <div style={{
            marginTop: 12, padding: 20, background: '#f8fafc',
            border: '1px solid #e2e8f0', borderRadius: 12, fontFamily: 'system-ui',
        }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                    <label style={labelStyle}>Promotion Name</label>
                    <input style={inputStyle} placeholder="e.g. January 2026 Giveaway" value={form.promotion} onChange={e => setForm({ ...form, promotion: e.target.value })} />
                </div>
                <div>
                    <label style={labelStyle}>Prize</label>
                    <input style={inputStyle} placeholder="e.g. $500 Gas Card" value={form.prize} onChange={e => setForm({ ...form, prize: e.target.value })} />
                </div>
                <div>
                    <label style={labelStyle}>First Name</label>
                    <input style={inputStyle} value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
                </div>
                <div>
                    <label style={labelStyle}>Last Name</label>
                    <input style={inputStyle} value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
                </div>
                <div>
                    <label style={labelStyle}>City</label>
                    <input style={inputStyle} value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
                </div>
                <div>
                    <label style={labelStyle}>State</label>
                    <input style={inputStyle} placeholder="e.g. PA" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} />
                </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
                <button
                    onClick={() => onSubmit(form)}
                    disabled={!form.promotion || !form.first_name || !form.last_name}
                    style={{
                        background: '#22c55e', color: 'white', border: 'none',
                        padding: '8px 18px', borderRadius: 8, fontSize: 13,
                        fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
                        opacity: (!form.promotion || !form.first_name || !form.last_name) ? 0.5 : 1,
                    }}
                >
                    Add Winner
                </button>
                <button
                    onClick={onCancel}
                    style={{
                        background: '#6b7280', color: 'white', border: 'none',
                        padding: '8px 18px', borderRadius: 8, fontSize: 13,
                        fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
                    }}
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};
